import React from 'react';
import { Alert, Button, DatePicker, Progress, Space, Spin, Tag, Typography, Upload } from 'antd';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Help, MEASURE, Panel, S } from '../ui';

const { Text } = Typography;

interface Props {
  /** When each of the four indices was last computed, or null for one that never ran. */
  indicesComputedAt?: Record<string, string | null> | null;
  indicesSheets?: { data_url: string | null; calculation_url: string | null } | null;
  /** Which half of the update is running, so one button can say what it is doing. */
  phase: 'idle' | 'generating' | 'reading';
  /** How many of the four indices this run has rewritten, and when that last changed. */
  recompute?: { done: number; total: number; since: number } | null;

  asOf: string;
  onAsOf: (value: string) => void;

  /**
   * The pricing workbook, held by the orchestrator rather than by this step.
   *
   * The step unmounts when you move to the next one, so a selection kept here would be
   * lost by going forward and coming back -- which matters now the file is required.
   */
  cbmFile: File | null;
  onCbmFile: (file: File | null) => void;
  /** Whether the recompute has already run since this screen was opened. */
  alreadyGenerated: boolean;
  proposal: any | null;

  publishedVersion: number | null;
  onLoadPublished: () => void;
  draftUpdatedAt?: string | null;
  draftUpdatedBy?: string | null;
  onLoadDraft: () => void;
  onStartBlank: () => void;

  loaded: boolean;
}

/**
 * How far the recompute has got.
 *
 * The wait is about two minutes and it has real milestones: the four indices are written
 * one at a time, and the poll already counts how many this run has rewritten. So the
 * count sets the floor and the words, and the clock only fills the gap between
 * milestones.
 *
 * The gaps are uneven and the total is not stable -- two measured runs took 127s and
 * 149s, with the first two indices taking 75% and 78% of the time -- so a curve tuned to
 * per-index budgets would sit still for half a minute in the middle of one run and finish
 * early on the next. Instead the bar eases towards the next milestone and never arrives:
 * always moving, never claiming a write that has not landed. One time constant, no
 * per-index numbers to drift out of date.
 *
 * No estimate of time remaining. It would have to come from those same unstable segment
 * lengths, and a write resetting the clock while dropping one segment from the total
 * makes any such estimate jump upwards, which reads worse than no estimate at all.
 */
const CREEP_TIME_CONSTANT_MS = 25_000;

const RecomputeProgress: React.FC<{ done: number; total: number; since: number }> = ({
  done, total, since,
}) => {
  // The bar has to move between writes, so it re-renders on a timer of its own rather
  // than waiting for a poll to change the count.
  const [, tick] = React.useState(0);
  React.useEffect(() => {
    const timer = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const steps = Math.max(total, 1);
  const span = 100 / steps;
  const elapsed = Math.max(Date.now() - since, 0);
  // 0.9 of the span: approaches the next milestone, stops short of it.
  const crept = span * 0.9 * (1 - Math.exp(-elapsed / CREEP_TIME_CONSTANT_MS));
  const complete = done >= steps;
  const percent = complete ? 100 : Math.min(done * span + crept, 100);

  return (
    <div style={{ maxWidth: MEASURE }}>
      <Progress
        percent={Math.round(percent)}
        status={complete ? 'success' : 'active'}
        showInfo={false}
        // No strokeColor: the bar takes colorPrimary, and success takes colorSuccess,
        // from the theme. See the note at the top of ui.tsx.
      />
      <Text type="secondary">
        {complete
          ? 'All four indices are current. Reading the workbook.'
          : done === 0
            ? 'Recomputing the integrity indices. The first of four takes the longest, around half a minute.'
            : `Recomputed ${done} of ${steps} indices.`}
      </Text>
    </div>
  );
};

/**
 * Says how fresh the computed indices are.
 *
 * Shows the OLDEST of the four and names the laggards when they disagree: a recompute
 * that fails partway leaves some series current and others a month behind, and the newest
 * timestamp would present that as fully up to date.
 */
/** The longest a single recompute of all four series is expected to take. */
const ONE_RUN_MS = 30 * 60 * 1000;

const ComputedStatus: React.FC<{
  busy: boolean;
  /** True once this session has recomputed, so the panel can report the run it just did. */
  justRan?: boolean;
  computedAt?: Record<string, string | null> | null;
}> = ({ busy, justRan, computedAt }) => {
  if (busy) {
    return (
      <Text type="secondary">
        Recomputing from retirement and issuance data. Around two minutes.
      </Text>
    );
  }

  const entries = Object.entries(computedAt ?? {});
  if (!entries.length || entries.every(([, v]) => !v)) {
    return <Text type="secondary">Never computed. Run this first.</Text>;
  }

  /*
    A run this session is reported as such.

    The alternative is inferring it from timestamps that arrive on a separate request, and
    a failed recompute surfaces as an error dialog rather than as silence -- so there is
    nothing this could be hiding.
  */
  if (justRan) {
    return <Text type="secondary">Recomputed a moment ago, all four indices.</Text>;
  }

  const never = entries.filter(([, v]) => !v).map(([k]) => k);
  const dated = entries.filter(([, v]) => !!v) as Array<[string, string]>;
  const times = dated.map(([, v]) => v).sort();
  const oldest = times[0];
  const newest = times[times.length - 1];

  /*
    One run writes the four series in sequence, which takes a minute or two.

    This compared calendar dates, which gets it wrong in both directions: a run started
    before midnight and finished after it looked like a failure, and a series left a
    fortnight behind looked fine as long as something else ran today.
  */
  const spreadMs = new Date(newest).getTime() - new Date(oldest).getTime();
  const disagree = never.length > 0 || spreadMs > ONE_RUN_MS;

  return (
    <Text type={disagree ? 'warning' : 'secondary'}>
      {disagree ? (
        <>
          The four indices were not computed together: oldest{' '}
          {new Date(oldest).toLocaleString()}
          {never.length ? `, and ${never.join(', ')} never ran` : ''}. Run again so they agree.
        </>
      ) : (
        <>Last computed {new Date(newest).toLocaleString()}.</>
      )}
    </Text>
  );
};

/**
 * Step one: get this month's numbers into the form.
 *
 * Recompute and read used to be two buttons, and a month's update presses both every time:
 * one click did a minute of work, the second did the only thing you could see. They are one
 * action now, with the label saying which half is running.
 *
 * The recompute stays explicit rather than implicit, though. It is heavy and it rewrites
 * the team's tracking sheets, so "just read the latest" exists beside it for when it has
 * already run today -- by Archit, or by you before fixing a workbook and reading again.
 */
const StepGenerate: React.FC<Props> = ({
  indicesComputedAt, indicesSheets, phase, recompute,
  asOf, onAsOf,
  cbmFile, onCbmFile, alreadyGenerated, proposal,
  publishedVersion, onLoadPublished, draftUpdatedAt, draftUpdatedBy, onLoadDraft, onStartBlank,
  loaded,
}) => {
  const busy = phase !== 'idle';

  return (
    <Space direction="vertical" size={S.lg} style={{ width: '100%' }}>
      <Panel
        title="Update this month"
        aside={loaded ? <Tag color="green">numbers loaded</Tag> : undefined}
      >
        <Help>
          Choose this month's pricing workbook and the rest follows: the platform recomputes
          the integrity indices from our own retirement and issuance data, then reads them
          and the workbook into the form. Nothing is saved until you save, and the commentary
          is never touched.
        </Help>

        <Space wrap align="center">
          <Upload
            accept=".xlsx"
            maxCount={1}
            showUploadList={false}
            disabled={busy}
            // Choosing the file starts the update. There is no button after it: pressing
            // one would only confirm the instruction that picking a file already gave.
            beforeUpload={(f) => { onCbmFile(f as unknown as File); return false; }}
          >
            <Button type={cbmFile ? 'default' : 'primary'} size="large" disabled={busy}>
              {cbmFile ? 'Change pricing file' : 'Choose the pricing workbook'}
            </Button>
          </Upload>

          {busy && !recompute ? (
            <Text type="secondary">
              <Spin size="small" style={{ marginRight: S.xs }} />
              {phase === 'generating' ? 'Generating the indices' : 'Reading the numbers'}
            </Text>
          ) : busy && recompute ? (
            <Text type="secondary">{(cbmFile as any)?.name}</Text>
          ) : cbmFile ? (
            <Text type="secondary">{(cbmFile as any).name}</Text>
          ) : (
            <Text type="secondary">
              A new CBM file arrives each month. Choosing it starts this month's update.
            </Text>
          )}
        </Space>

        {recompute && (
          <div style={{ marginTop: S.md }}>
            <RecomputeProgress
              done={recompute.done}
              total={recompute.total}
              since={recompute.since}
            />
          </div>
        )}

        {phase === 'reading' && alreadyGenerated && (
          <Text type="secondary" style={{ display: 'block', marginTop: S.sm }}>
            The indices were generated a moment ago, so only the workbook is being read.
          </Text>
        )}


        <div style={{ marginTop: S.sm }}>
          {/*
            Busy for the whole run, not just its first half.

            This was `phase === 'generating'`, so during the reading half -- and in the gap
            before the refetched draft lands -- it rendered the timestamps from BEFORE the
            run. Mid-run those are genuinely mixed, some series rewritten and some not, so
            it told you the indices disagreed and to run again, about a run that was still
            going and would finish consistent.
          */}
          {/* The bar says what is happening while it is up; this is the resting state. */}
          {!recompute && (
            <ComputedStatus
              busy={busy}
              justRan={alreadyGenerated}
              computedAt={indicesComputedAt}
            />
          )}
        </div>

        {(indicesSheets?.data_url || indicesSheets?.calculation_url) && (
          <Space wrap size={S.md} style={{ marginTop: S.sm }}>
            <Text type="secondary" style={{ fontSize: 12 }}>What the run writes:</Text>
            {indicesSheets?.data_url && (
              <Typography.Link href={indicesSheets.data_url} target="_blank" rel="noopener noreferrer">
                Data sheet with indices
              </Typography.Link>
            )}
            {indicesSheets?.calculation_url && (
              <Typography.Link href={indicesSheets.calculation_url} target="_blank" rel="noopener noreferrer">
                Calculation sheet
              </Typography.Link>
            )}
          </Space>
        )}

        {proposal && !!proposal.errors?.length && (
          <Alert
            style={{ marginTop: S.md }}
            type="error"
            showIcon
            message="Could not read the sources"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {proposal.errors.map((e: string) => <li key={e}>{e}</li>)}
              </ul>
            }
          />
        )}

        {proposal && !proposal.errors?.length && (
          <Alert
            style={{ marginTop: S.md }}
            type="success"
            showIcon
            message="Numbers are in"
            description={
              <>
                <Text>
                  Already in the form below and in the next two steps. Nothing is saved yet,
                  and each summary is written beside the chart it describes.
                </Text>
                {!!proposal.warnings?.length && (
                  <ul style={{ margin: `${S.sm}px 0 0`, paddingLeft: 18 }}>
                    {proposal.warnings.map((w: string) => (
                      <li key={w}><Text type="warning">{w}</Text></li>
                    ))}
                  </ul>
                )}
                {/* Detail on demand: it matters when a number looks wrong, not before. */}
                {!!proposal.provenance?.length && (
                  <details style={{ marginTop: S.sm }}>
                    <summary style={{ cursor: 'pointer' }}>
                      <Text type="secondary">Where each value came from</Text>
                    </summary>
                    <ul style={{ margin: `${S.xs}px 0 0`, paddingLeft: 18 }}>
                      {proposal.provenance.map((p: any) => (
                        <li key={p.field}>
                          <Text code>{p.field}</Text> <Text type="secondary">{p.source}</Text>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </>
            }
          />
        )}
      </Panel>

      <Panel title="Or start from something else">
        <Help>
          Useful for a correction, or to pick up where you left off. Both fill the same
          form, so you can still change anything in the steps that follow.
        </Help>
        <Space direction="vertical" size={S.sm} style={{ width: '100%' }}>
          {draftUpdatedAt && (
            <Space wrap align="center">
              <Button icon={<EditOutlined />} onClick={onLoadDraft}>Continue the saved draft</Button>
              <Text type="secondary">
                Last saved {new Date(draftUpdatedAt).toLocaleString()}
                {draftUpdatedBy ? ` by ${draftUpdatedBy}` : ''}. Not public.
              </Text>
            </Space>
          )}
          <Space wrap align="center">
            <Button onClick={onLoadPublished} disabled={publishedVersion == null}>
              Copy the live page
            </Button>
            <Text type="secondary">
              {publishedVersion == null
                ? 'Nothing has been published yet.'
                : `Copies version ${publishedVersion}, commentary included.`}
            </Text>
          </Space>
          <Space wrap align="center">
            <Button icon={<FormOutlined />} onClick={onStartBlank}>Start with an empty form</Button>
            <Text type="secondary">Type everything yourself.</Text>
          </Space>
        </Space>
      </Panel>

      {loaded && (
        <Panel title="The date this data is from">
          <Help>
            It decides every QTD and YTD marker on the page: a period that has not finished
            by this date shows as in progress. Taken from the last computation, and
            editable if that is not the cut you mean.
          </Help>
          <DatePicker
            value={asOf ? dayjs(asOf) : null}
            onChange={(d) => onAsOf(d ? d.format('YYYY-MM-DD') : '')}
            format="D MMM YYYY"
            allowClear={false}
          />
        </Panel>
      )}
    </Space>
  );
};

export default StepGenerate;
